import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import swiperStyles from '@commonStyles/Swiper.module.scss'
import s from './StackedGallery.module.scss'
import type { Maybe, ProductVideo, ProductImage } from '@pylot-data/pylotschema'
import { useTranslation } from 'next-i18next/pages'
import { Button } from '@corratech/pylot-ui'
import dynamic from 'next/dynamic'
import ImageSlide from '../util/ImageSlide'
import {
    getFirstGalleryImageIndex,
    getGalleryMediaKey
} from '../util/getGalleryMediaKey'
import VideoPlayer from '../VideoPlayer'

const FullscreenGallery = dynamic(() => import('../FullscreenGallery'))

type ProductGalleryType = {
    alt?: string
    mediaGalleryEntries: Maybe<ProductVideo | ProductImage>[]
    zoom: boolean
    showFullscreen: boolean
    imagesPerPage?: number
    loadMoreLabel?: string
    enableLastVideoAsFullWidth?: boolean
    badgeLabel?: string
    badgeVariant?: 'low-stock'
}

const StackedGallery = ({
    alt,
    mediaGalleryEntries,
    zoom,
    showFullscreen,
    imagesPerPage = 0, // if 0, then show all and disable loadmore feature
    loadMoreLabel,
    enableLastVideoAsFullWidth = false,
    badgeLabel,
    badgeVariant
}: ProductGalleryType): ReactElement => {
    const { t } = useTranslation(['common'])

    const [isFullscreen, setFullscreen] = useState(false)
    const [showingImagesCount, setShowingImagesCount] =
        useState<ProductGalleryType['imagesPerPage']>(imagesPerPage)

    const totalOfImages =
        mediaGalleryEntries.filter(
            (item) => item?.__typename === 'ProductImage'
        ).length || 0

    const closeFullscreen = () => setFullscreen(false)

    const [sliderIndex, setSliderIndex] = useState(0)

    const openFullscreen = (i: number) => {
        setSliderIndex(i)
        setFullscreen(true)
    }

    const firstImageIndex = useMemo(
        () => getFirstGalleryImageIndex(mediaGalleryEntries),
        [mediaGalleryEntries]
    )

    const renderImages = (
        mediaGalleryData: Maybe<ProductVideo | ProductImage>[],
        alt?: string,
        width = 1050,
        height = 1050
    ) =>
        mediaGalleryData?.map((image, i) => (
            <div
                key={getGalleryMediaKey(image, i)}
                className={cn(s['gallery-container'], {
                    [s['video-player']]:
                        image?.__typename === 'ProductVideo' &&
                        i === mediaGalleryData.length - 1 &&
                        enableLastVideoAsFullWidth
                })}
            >
                {image?.__typename === 'ProductVideo' ? (
                    <VideoPlayer
                        alt={alt || t('Product Thumbnail')}
                        url={`${image.video_content?.video_url!}`}
                        index={i}
                        image={image?.url}
                        label={image?.label}
                    />
                ) : imagesPerPage > 0 ? (
                    i <= showingImagesCount! && (
                        <div>
                            {image?.arLink ? (
                                <iframe
                                    title="test"
                                    src={image?.url || ''}
                                    frameBorder="0"
                                />
                            ) : (
                                <ImageSlide
                                    image={image}
                                    alt={alt || t('Product Image')}
                                    width={width}
                                    height={height}
                                    index={i}
                                    priority={i === firstImageIndex}
                                    zoom={zoom}
                                    showFullscreen={showFullscreen}
                                    s={s}
                                    openFullscreen={openFullscreen}
                                />
                            )}
                        </div>
                    )
                ) : (
                    <ImageSlide
                        image={image}
                        alt={alt || t('Product Image')}
                        width={width}
                        height={height}
                        index={i}
                        priority={i === firstImageIndex}
                        zoom={zoom}
                        showFullscreen={showFullscreen}
                        s={s}
                        openFullscreen={openFullscreen}
                    />
                )}
            </div>
        ))

    useEffect(() => {
        const html = document.getElementsByTagName('html')[0]
        html.classList.remove('zoom-open')
        if (isFullscreen) html.classList.add('zoom-open')
    }, [isFullscreen])

    const handleLoadMoreClick = () => {
        setShowingImagesCount((currentValue) => currentValue! + imagesPerPage)
    }

    return (
        <div className={s['pdp-wrapper']}>
            {badgeLabel && (
                <span
                    className={cn(s['pdp-badge'], {
                        [s['pdp-badge-low-stock']]: badgeVariant === 'low-stock'
                    })}
                >
                    {badgeLabel}
                </span>
            )}
            <div className={cn(s['pdp-slider'], swiperStyles.root)}>
                {isFullscreen && (
                    <FullscreenGallery
                        alt={alt || t('Product Image')}
                        closeFullscreen={closeFullscreen}
                        mediaGalleryEntries={mediaGalleryEntries}
                        sliderIndex={sliderIndex}
                    />
                )}
                {renderImages(mediaGalleryEntries, alt || t('Product Image'))}
                {imagesPerPage > 0 && showingImagesCount! < totalOfImages && (
                    <div className={s['load-more-button']}>
                        <Button
                            variant="secondary"
                            onClick={handleLoadMoreClick}
                        >
                            {loadMoreLabel || t('Load More')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default StackedGallery
